/**
 * LegalPageShell — shared layout for all legal pages
 *
 * v1.0 - 2026-03-23 - Full shell: sticky bar, hero, TOC sidebar, scroll-spy, print styles
 *
 * Exports:
 *   LEGAL_STICKY_BAR_HEIGHT  — use for scrollMarginTop on section anchors
 *   legalBodyTextSx          — shared body-text style (text.primary, dark-mode-safe)
 *   LegalSection             — { id, label } interface for sections prop
 *   default LegalPageShell   — the shell component
 */
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Container,
  Divider,
  FormControl,
  MenuItem,
  Select,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PrintIcon from '@mui/icons-material/Print';

// ─── Public constants ─────────────────────────────────────────────────────────

/** Height of the sticky bar in px. Use as scrollMarginTop on section anchors. */
export const LEGAL_STICKY_BAR_HEIGHT = 52;

/** Dark-mode-safe body text style. Use on all legal prose. */
export const legalBodyTextSx = { color: 'text.primary', lineHeight: 1.6 } as const;

// ─── Types ────────────────────────────────────────────────────────────────────

export interface LegalSection {
  id: string;
  label: string;
}

interface LegalPageShellProps {
  title: string;
  lastUpdated: string;
  sections: LegalSection[];
  children: React.ReactNode;
}

// ─── Component ────────────────────────────────────────────────────────────────

const LegalPageShell: React.FC<LegalPageShellProps> = ({
  title,
  lastUpdated,
  sections,
  children,
}) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [activeId, setActiveId] = useState<string>(sections[0]?.id ?? '');
  const observerRef = useRef<IntersectionObserver | null>(null);

  // ── Back navigation ────────────────────────────────────────────────────────
  const handleBack = () => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      window.location.href = '/';
    }
  };

  // ── Smooth scroll to section ───────────────────────────────────────────────
  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // ── Scroll-spy via IntersectionObserver ────────────────────────────────────
  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        // Pick the first intersecting entry (topmost visible section)
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length > 0) {
          setActiveId(visible[0].target.id);
        }
      },
      {
        // rootMargin: push the detection zone below the sticky bar
        rootMargin: `-${LEGAL_STICKY_BAR_HEIGHT + 8}px 0px -60% 0px`,
        threshold: 0,
      }
    );

    sections.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observerRef.current!.observe(el);
    });

    return () => {
      observerRef.current?.disconnect();
    };
  }, [sections]);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>

      {/* Print styles */}
      <style>{`
        @media print {
          .legal-sticky-bar { display: none !important; }
          .legal-toc-sidebar { display: none !important; }
          .legal-mobile-toc { display: none !important; }
          body { background-color: white !important; color: black !important; }
          * { background: transparent !important; color: black !important; box-shadow: none !important; }
        }
      `}</style>

      {/* ── Sticky top bar ──────────────────────────────────────────────────── */}
      <Box
        className="legal-sticky-bar"
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1200,
          width: '100%',
          height: LEGAL_STICKY_BAR_HEIGHT,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: { xs: 2, sm: 3 },
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          boxSizing: 'border-box',
        }}
      >
        {/* Left: back button */}
        <Button
          size="small"
          startIcon={<ArrowBackIcon sx={{ fontSize: '1rem' }} />}
          onClick={handleBack}
          sx={{
            color: 'text.secondary',
            textTransform: 'none',
            whiteSpace: 'nowrap',
            fontWeight: 400,
            '&:hover': { color: 'primary.main', bgcolor: 'transparent' },
          }}
        >
          Back to MyLojic
        </Button>

        {/* Center: last updated */}
        <Typography
          variant="caption"
          sx={{
            color: 'text.disabled',
            display: { xs: 'none', sm: 'block' },
            whiteSpace: 'nowrap',
          }}
        >
          Last Updated: {lastUpdated}
        </Typography>

        {/* Right: print */}
        <Button
          size="small"
          startIcon={<PrintIcon sx={{ fontSize: '1rem' }} />}
          onClick={() => window.print()}
          sx={{
            color: 'text.secondary',
            textTransform: 'none',
            whiteSpace: 'nowrap',
            fontWeight: 400,
            '&:hover': { color: 'primary.main', bgcolor: 'transparent' },
          }}
        >
          {/* Shorten on mobile */}
          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
            Save or Print a Copy
          </Box>
          <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
            Print
          </Box>
        </Button>
      </Box>

      {/* ── Hero block ──────────────────────────────────────────────────────── */}
      <Box
        sx={{
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          py: { xs: 4, sm: 6 },
          textAlign: 'center',
        }}
      >
        <Typography
          variant="h2"
          sx={{ fontWeight: 700, color: 'primary.main', mb: 1, fontSize: { xs: '2rem', sm: '3rem' } }}
        >
          {title}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          MyLojic Platform&nbsp;|&nbsp;www.mylojic.com&nbsp;|&nbsp;Data Edge Services LLC
        </Typography>
      </Box>

      {/* ── Main body: TOC sidebar + content ────────────────────────────────── */}
      <Container
        maxWidth="xl"
        sx={{ py: { xs: 3, sm: 5 } }}
        disableGutters={false}
      >
        <Box sx={{ display: 'flex', gap: { md: 4 }, alignItems: 'flex-start' }}>

          {/* TOC sidebar — desktop only */}
          {isDesktop && sections.length > 0 && (
            <Box
              className="legal-toc-sidebar"
              component="nav"
              aria-label="Table of contents"
              sx={{
                width: 240,
                flexShrink: 0,
                position: 'sticky',
                top: LEGAL_STICKY_BAR_HEIGHT + 16,
                maxHeight: `calc(100vh - ${LEGAL_STICKY_BAR_HEIGHT + 48}px)`,
                overflowY: 'auto',
                pb: 2,
              }}
            >
              <Typography
                variant="overline"
                sx={{
                  color: 'text.disabled',
                  fontWeight: 700,
                  letterSpacing: 2,
                  mb: 1,
                  display: 'block',
                  fontSize: '0.65rem',
                }}
              >
                Contents
              </Typography>
              <Divider sx={{ mb: 1.5 }} />
              {sections.map(({ id, label }) => {
                const isActive = activeId === id;
                return (
                  <Box
                    key={id}
                    onClick={() => scrollToSection(id)}
                    sx={{
                      px: 1.5,
                      py: 0.75,
                      mb: 0.25,
                      cursor: 'pointer',
                      borderLeft: '2px solid',
                      borderColor: isActive ? 'primary.main' : 'transparent',
                      color: isActive ? 'primary.main' : 'text.secondary',
                      fontSize: '0.8125rem',
                      lineHeight: 1.4,
                      borderRadius: '0 4px 4px 0',
                      bgcolor: isActive ? 'action.selected' : 'transparent',
                      transition: 'all 0.15s ease',
                      '&:hover': {
                        color: 'primary.main',
                        bgcolor: 'action.hover',
                      },
                    }}
                  >
                    {label}
                  </Box>
                );
              })}
            </Box>
          )}

          {/* Content column */}
          <Box sx={{ flex: 1, minWidth: 0 }}>

            {/* Mobile TOC dropdown */}
            {!isDesktop && sections.length > 0 && (
              <Box className="legal-mobile-toc" sx={{ mb: 3, px: { xs: 2, sm: 0 } }}>
                <FormControl fullWidth size="small">
                  <Select
                    value={activeId || sections[0]?.id}
                    onChange={(e: SelectChangeEvent) => {
                      setActiveId(e.target.value);
                      scrollToSection(e.target.value);
                    }}
                  >
                    {sections.map(({ id, label }) => (
                      <MenuItem key={id} value={id}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}

            {/* Page content */}
            {children}

          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default LegalPageShell;
